import {
    TypedChartOfAccountsTemplateUpload,
    TypedChartOfAccountsTemplateUploadRow,
} from "@common/helpers/chartOfAccountsTemplateTypeMapping";
import { pairCustomAccounts } from "@common/helpers/domains/accounting/customRowPairing";
import { createTemplateCodeClaimer } from "@common/helpers/domains/accounting/templateAccountCodes";
import { addAccountInScope, updateAccount } from "./draftAccountOperations";
import { createGroup } from "./draftGroupOperations";
import { DraftAccount, DraftState, GenerateId } from "./draftState";

interface MergeImportedChartParams {
    state: DraftState;
    chart: TypedChartOfAccountsTemplateUpload;
    entityId: number;
    generateId: GenerateId;
}

export interface MergeImportedChartResult {
    state: DraftState;
    addedAccountCount: number;
    skippedAccountCount: number;
    clearedCodeCount: number;
}

const bySortOrder = (
    first: TypedChartOfAccountsTemplateUploadRow,
    second: TypedChartOfAccountsTemplateUploadRow,
): number => first.sortOrder - second.sortOrder;

const orderGroupsParentFirst = (
    groups: TypedChartOfAccountsTemplateUploadRow[],
): TypedChartOfAccountsTemplateUploadRow[] => {
    const visit = (
        parentGroupId: string | null,
    ): TypedChartOfAccountsTemplateUploadRow[] =>
        groups
            .filter((group) => group.parentGroupId === parentGroupId)
            .sort(bySortOrder)
            .flatMap((group) => [group, ...visit(group.id)]);
    return visit(null);
};

const collectAncestorGroupIds = ({
    groups,
    accounts,
}: TypedChartOfAccountsTemplateUpload): Set<string> => {
    const parentIdById = new Map(
        groups.map((group) => [group.id, group.parentGroupId]),
    );
    const ancestorIds = new Set<string>();
    for (const account of accounts) {
        let groupId = account.parentGroupId;
        while (groupId !== null && !ancestorIds.has(groupId)) {
            ancestorIds.add(groupId);
            groupId = parentIdById.get(groupId) ?? null;
        }
    }
    return ancestorIds;
};

const claimExistingCodes = (accounts: DraftAccount[]) => {
    const claim = createTemplateCodeClaimer();
    accounts.forEach((account) => claim(account.codeText));
    return claim;
};

export const mergeImportedChart = ({
    state,
    chart,
    entityId,
    generateId,
}: MergeImportedChartParams): MergeImportedChartResult => {
    const pairedDraftIdByImportedId = pairCustomAccounts({
        template: {
            accounts: chart.accounts.map((account) => ({
                ...account,
                type: account.kickType,
            })),
            groups: chart.groups,
        },
        entity: state,
    });
    const addedAccounts = chart.accounts
        .filter(({ id }) => !pairedDraftIdByImportedId.has(id))
        .sort(bySortOrder);
    const usedGroupIds = collectAncestorGroupIds({
        groups: chart.groups,
        accounts: addedAccounts,
    });

    const draftGroupIdByImportedId = new Map<string, string>();
    const resolveParent = (importedParentId: string | null): string | null =>
        importedParentId === null
            ? null
            : (draftGroupIdByImportedId.get(importedParentId) ?? null);

    const withGroups = orderGroupsParentFirst(chart.groups)
        .filter(({ id }) => usedGroupIds.has(id))
        .reduce((current, group) => {
            const created = createGroup({
                state: current,
                entityId,
                type: group.kickType,
                parentGroupId: resolveParent(group.parentGroupId),
                name: group.name.trim(),
                generateId,
            });
            draftGroupIdByImportedId.set(group.id, created.groupId);
            return created.state;
        }, state);

    const claimCode = claimExistingCodes(state.accounts);
    let clearedCodeCount = 0;
    const merged = addedAccounts.reduce((current, account) => {
        const isCodeKept = claimCode(account.codeText);
        if (!isCodeKept) {
            clearedCodeCount += 1;
        }
        const added = addAccountInScope({
            state: current,
            entityId,
            type: account.kickType,
            parentGroupId: resolveParent(account.parentGroupId),
            generateId,
            codeText: isCodeKept ? account.codeText : null,
        });
        return updateAccount({
            state: added.state,
            id: added.accountId,
            patch: { name: account.name },
        });
    }, withGroups);

    return {
        state: merged,
        addedAccountCount: addedAccounts.length,
        skippedAccountCount: pairedDraftIdByImportedId.size,
        clearedCodeCount,
    };
};
